import {useLayoutEffect, useState} from 'react';
import {Path, useForm} from 'react-hook-form';
import {
  Brand,
  BrandSection,
  brandSectionKeys,
  CreateBrand,
  CreateBrandRequest,
  createBrandSchema,
  Organization,
} from '@tinct/core-api-models';
import {zodResolver} from '@hookform/resolvers/zod';
import {
  ApiError,
  isBrandVersionConflict,
  useCreateBrand,
  useGetOrganizationById,
} from '@tinct/core-api';
import {handleApiError} from '@tinct/web-form';
import {toast} from 'sonner';

type UseCreateOrganizationBrandFormOptions = {
  organizationId: string | null;
  /**
   * The top-level brand sections this form edits. A save creates a whole new
   * brand version (versions are immutable): these sections come from the form,
   * every other one from the version the form was loaded from, so a form can
   * never change a section it does not display.
   */
  sections: readonly BrandSection[];
  onSuccess?: () => void;
};

/**
 * Every top-level section of a brand version, as the API returned it: nulls
 * are kept as they are, never replaced by the form's defaults.
 */
type RawBrandSections = Record<BrandSection, unknown>;

/**
 * The brand version a form's save is based on: its number (null when the
 * organization has no brand yet) and its raw sections, which the save carries
 * unchanged for every section the form does not edit.
 */
type BaseVersion = { version: number | null; values: RawBrandSections };

const pickSections = (source: Partial<Record<BrandSection, unknown>>): RawBrandSections =>
  Object.fromEntries(brandSectionKeys.map((section) => [section, source[section] ?? null])) as RawBrandSections;

export const BRAND_CHANGED_MESSAGE =
  'This brand was changed since you opened the page (by the brand extraction or another tab). ' +
  'The latest values have been loaded and your unsaved changes were discarded: please make them again.';

const getDefaultValues = (organizationId: string | null): CreateBrand => ({
  organization: { id: organizationId as string },
  name: '',
  logo: null,
  businessProfile: {
    industry: '',
    businessModel: '',
    targetCustomer: '',
    geography: null,
    languages: null,
  },
  valueAndMessaging: {
    brandMessaging: '',
    keyBenefits: '',
    painPointsAddressed: '',
    differentiators: '',
    vision: '',
    toneOfVoices: null,
    keywords: null,
    whatToAvoid: '',
    negativeKeywords: null,
  },
  visualIdentity: {
    primaryColor: null,
    secondaryColors: null,
    typography: null,
    imageryStyle: null,
    whatToAvoid: null,
  },
  compliance: {
    legalLink: '',
    industryCompliance: '',
    contentRestriction: '',
  },
  qualityControl: {
    legalCompliance: 60,
    brandConsistency: 60,
    messagingAlignment: 60,
  },
  personalization: {
    depth: null,
    useLogo: true,
  },
});

const toFormValues = (organizationId: string | null, brand: Brand | null | undefined): CreateBrand => {
  const defaults = getDefaultValues(organizationId);
  if (!brand) {
    return defaults;
  }
  return {
    organization: { id: organizationId as string },
    name: brand.name ?? defaults.name,
    logo: brand.logo ?? defaults.logo,
    businessProfile: {
      industry: brand.businessProfile?.industry ?? defaults.businessProfile.industry,
      businessModel: brand.businessProfile?.businessModel ?? defaults.businessProfile.businessModel,
      targetCustomer: brand.businessProfile?.targetCustomer ?? defaults.businessProfile.targetCustomer,
      geography: brand.businessProfile?.geography ?? defaults.businessProfile.geography,
      languages: brand.businessProfile?.languages ?? defaults.businessProfile.languages,
    },
    valueAndMessaging: {
      brandMessaging: brand.valueAndMessaging?.brandMessaging ?? defaults.valueAndMessaging.brandMessaging,
      keyBenefits: brand.valueAndMessaging?.keyBenefits ?? defaults.valueAndMessaging.keyBenefits,
      painPointsAddressed: brand.valueAndMessaging?.painPointsAddressed ?? defaults.valueAndMessaging.painPointsAddressed,
      differentiators: brand.valueAndMessaging?.differentiators ?? defaults.valueAndMessaging.differentiators,
      vision: brand.valueAndMessaging?.vision ?? defaults.valueAndMessaging.vision,
      toneOfVoices: brand.valueAndMessaging?.toneOfVoices ?? defaults.valueAndMessaging.toneOfVoices,
      keywords: brand.valueAndMessaging?.keywords ?? defaults.valueAndMessaging.keywords,
      whatToAvoid: brand.valueAndMessaging?.whatToAvoid ?? defaults.valueAndMessaging.whatToAvoid,
      negativeKeywords: brand.valueAndMessaging?.negativeKeywords ?? defaults.valueAndMessaging.negativeKeywords,
    },
    visualIdentity: {
      primaryColor: brand.visualIdentity?.primaryColor ?? defaults.visualIdentity.primaryColor,
      secondaryColors: brand.visualIdentity?.secondaryColors ?? defaults.visualIdentity.secondaryColors,
      typography: brand.visualIdentity?.typography ?? defaults.visualIdentity.typography,
      imageryStyle: brand.visualIdentity?.imageryStyle ?? defaults.visualIdentity.imageryStyle,
      whatToAvoid: brand.visualIdentity?.whatToAvoid ?? defaults.visualIdentity.whatToAvoid,
    },
    compliance: {
      legalLink: brand.compliance?.legalLink ?? defaults.compliance.legalLink,
      industryCompliance: brand.compliance?.industryCompliance ?? defaults.compliance.industryCompliance,
      contentRestriction: brand.compliance?.contentRestriction ?? defaults.compliance.contentRestriction,
    },
    qualityControl: {
      legalCompliance: brand.qualityControl?.legalCompliance ?? defaults.qualityControl.legalCompliance,
      brandConsistency: brand.qualityControl?.brandConsistency ?? defaults.qualityControl.brandConsistency,
      messagingAlignment: brand.qualityControl?.messagingAlignment ?? defaults.qualityControl.messagingAlignment,
    },
    personalization: {
      depth: brand.personalization?.depth ?? defaults.personalization.depth,
      useLogo: brand.personalization?.useLogo ?? defaults.personalization.useLogo,
    },
  };
};

const toBaseVersion = (organizationId: string | null, brand: Brand | null | undefined): BaseVersion => ({
  version: brand?.version ?? null,
  // With no brand yet there is nothing to carry over: the first version starts
  // from the defaults the form shows.
  values: brand ? pickSections(brand) : pickSections(getDefaultValues(organizationId)),
});

/** The paths of the fields the user edited, down to the field (a list counts as one field). */
const dirtyLeafPaths = (dirtyFields: object, prefix = ''): Path<CreateBrand>[] =>
  Object.entries(dirtyFields).flatMap(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return dirtyLeafPaths(value, path);
    }
    return value ? [path as Path<CreateBrand>] : [];
  });

/**
 * The save payload: the base version's raw sections, with only the form's own
 * sections taken from the form. A section the form does not edit is sent back
 * exactly as loaded (a null stays null); an owned one is taken whole, sub-fields
 * the form does not display included (they hold the loaded values).
 */
const toSavePayload = (
  data: CreateBrand,
  base: BaseVersion,
  sections: readonly BrandSection[],
): CreateBrandRequest => {
  const payload: Record<string, unknown> = {
    ...base.values,
    organization: data.organization,
    baseVersion: base.version,
  };
  for (const section of sections) {
    payload[section] = data[section];
  }
  // Raw sections may hold nulls the create schema does not type; the server
  // takes them as stored.
  return payload as CreateBrandRequest;
};

export const useCreateOrganizationBrandForm = ({
  organizationId,
  sections,
  onSuccess,
}: UseCreateOrganizationBrandFormOptions) => {
  const { data: organization, refetch } = useGetOrganizationById(
    {
      id: organizationId as string,
      fields: {
        id: null,
        name: null,
        logo: null,
        lastBrandVersion: {
          fields: {
            id: null,
            version: null,
            name: null,
            logo: null,
            businessProfile: null,
            valueAndMessaging: null,
            visualIdentity: null,
            compliance: null,
            qualityControl: null,
            personalization: null,
          },
        },
      },
    },
    {
      enabled: !!organizationId,
      // The brand can be (re)written outside this tab — by the extraction job
      // or another tab — so never trust the cached organization here: one
      // fresh GET per mount, then the layout effect below reloads the form.
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );

  const form = useForm<CreateBrand>({
    resolver: zodResolver(createBrandSchema),
    defaultValues: getDefaultValues(organizationId),
  });

  // The organization whose brand the form was last loaded from, and the brand
  // version the form's saves are based on. Null until the organization has
  // loaded: the form must not render (nor submit) before, as submitting the
  // empty defaults would overwrite the brand with them. A workspace with no
  // brand yet (lastBrandVersion null) loads the defaults, based on no version.
  const [loaded, setLoaded] = useState<{ organization: Organization; base: BaseVersion } | null>(null);
  const { isDirty } = form.formState;

  // Adjusted while rendering (not in an effect) so that the render showing the
  // form already knows which version it is based on.
  if (organization && organization !== loaded?.organization) {
    setLoaded({
      organization,
      // A clean form is about to hold exactly this version, so its saves are
      // based on it; an edited one keeps the version its edits were made on,
      // and the server answers 409 if that one is no longer the latest.
      base: loaded && isDirty ? loaded.base : toBaseVersion(organizationId, organization.lastBrandVersion),
    });
  }

  const loadedOrganization = loaded?.organization;

  // Each (re)load of the organization resets the form to the loaded values,
  // except the fields the user already edited: a refetch landing after the
  // user started typing must not wipe what they typed. (react-hook-form's own
  // `values` + `keepDirtyValues` is not used: it keeps a whole section as soon
  // as one of its fields is dirty, which would hide the refreshed neighbours.)
  // A layout effect, so the form is never painted, nor clickable, with the
  // defaults it is created with.
  useLayoutEffect(() => {
    if (!loadedOrganization) {
      return;
    }
    const edited = dirtyLeafPaths(form.formState.dirtyFields).map(
      (path) => [path, form.getValues(path)] as const,
    );
    form.reset(toFormValues(organizationId, loadedOrganization.lastBrandVersion));
    for (const [path, value] of edited) {
      form.setValue(path, value, { shouldDirty: true });
    }
  }, [loadedOrganization, organizationId, form]);

  const reloadLatestBrand = async () => {
    toast.error(BRAND_CHANGED_MESSAGE);
    const { data: fresh } = await refetch();
    if (fresh) {
      // The one case where the user's unsaved edits are dropped on purpose:
      // they were made on a version that is no longer the latest.
      form.reset(toFormValues(organizationId, fresh.lastBrandVersion));
      setLoaded({ organization: fresh, base: toBaseVersion(organizationId, fresh.lastBrandVersion) });
    }
  };

  const onSaved = (brand: Brand, { data: saved }: { data: CreateBrandRequest }) => {
    // The saved version is the new base: its values are exactly what was sent.
    // What the form holds is now saved, so the refetch that the mutation
    // triggers may then refresh every field.
    const values = pickSections(saved);
    setLoaded((previous) => previous && { ...previous, base: { version: brand.version ?? null, values } });
    form.reset(form.getValues());
    toast.success('Brand updated successfully');
    onSuccess?.();
  };

  const onSaveError = (error: ApiError) => {
    if (isBrandVersionConflict(error)) {
      void reloadLatestBrand();
      return;
    }
    handleApiError(error, form);
  };

  const createBrand = useCreateBrand({ onSuccess: onSaved, onError: onSaveError });

  const handleSubmit = form.handleSubmit(
    (data) => {
      if (!loaded) {
        return;
      }
      createBrand.mutate({
        data: toSavePayload(data, loaded.base, sections),
        fields: { id: null, version: null },
      });
    },
    (errors) => {
      console.error('[Zod Validation Errors]', errors);
    },
  );

  return {
    form,
    handleSubmit,
    isLoading: createBrand.isPending,
    /** True once the loaded values are in the form: render (and submit) it only then. */
    isReady: loaded !== null,
  };
};
